import { useContext, useEffect, useState } from "react";

import {
  CircularProgress,
  Tab,
  Tabs,
  Theme,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { BulletinResource, getBulletinById } from "@rubi/api-generated";
import { colors } from "@rubi/ui";
import { toast } from "@rubi/ui";
import {
  Panel,
  Group as PanelGroup,
  Separator as PanelResizeHandle,
} from "react-resizable-panels";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { isInternalPath } from "@/common/internalPath";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { IUserPermission } from "@/types";

import { Access } from "./access/Access";
import { DisplayAlert } from "./add/DisplayAlert";
import { DisplayAlertSingleTopButton } from "./add/DisplayAlert/DisplayAlertSingleTopButton";
import ResolveBulletin from "./add/DisplayAlert/ResolveBulletin";
import "./alerts.scss";
import { ALERT_STATUS } from "./constants";
import { AlertViewContext, SingleAlertContext } from "./context";
import ShareMultipleDialog from "./ShareMultipleDialog";
import { updateToContextStructure } from "./utils";

interface AlertSingleProps {
  mode: ALERT_STATUS;
  slideoutIdOverride?: number | null;
  queryNextPage?: () => void;
}

export const AlertSingle = ({
  mode,
  slideoutIdOverride,
  queryNextPage = () => {},
}: AlertSingleProps) => {
  const { currentItems, setScrollToBulletinId } = useContext(AlertViewContext);
  const { currentUser } = useContext(AuthStateContext);
  const { alertProperties, setAlertProperties } =
    useContext(SingleAlertContext);

  const { id } = useParams();
  const idToUse = (id && parseInt(id)) || slideoutIdOverride;
  const isAlertSingleView = location.pathname.includes("/single");
  const [alertPermissions] = useState(
    currentUser &&
      parseUserPermissions(currentUser?.permissions as IUserPermission[]),
  );

  const [currentItem, setCurrentItem] = useState<BulletinResource>(
    {} as BulletinResource,
  );
  const alertCreatorId = currentItem?.creator_user_id;

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isResolveOpen, setIsResolveOpen] = useState(false);
  const [isCreator, setIsCreator] = useState(
    currentUser?.id === alertCreatorId,
  );
  const [isLoaded, setIsLoaded] = useState(false);
  const readOnly = alertPermissions
    ? !alertPermissions.canCreateBulletin
    : false;

  const { resolutions } = alertProperties;
  const hasUnpublishedResolution = resolutions?.some(
    (resolution) =>
      !resolution.published &&
      (resolution.details?.length > 0 ||
        Object.values(resolution.attachments).length > 0 ||
        resolution.reason?.length > 0),
  );

  const {
    issuing_police_service,
    associated_crime,
    occurrence_number,
    issued_time,
    description,
    pdf,
    evidence_files,
  } = currentItem;
  const [searchParams, setSearchParams] = useSearchParams();
  const accessView = searchParams.get("access");

  useEffect(() => {
    const searchParams = window.location.search;
    if (searchParams.includes("share=true")) {
      setIsShareModalOpen(true);
    }
  }, []);

  const navigate = useNavigate();
  const { state: navState } = useLocation();
  // The board URL this bulletin was opened from (set by AlertsList), carried
  // along Previous/Next so Back always returns to that board.
  const from = (navState as { from?: string } | null)?.from;
  const returnTo = from && isInternalPath(from) ? from : undefined;

  const theme = useTheme();
  const isSmall = useMediaQuery((theme as Theme).breakpoints.down("sm"));

  const [mobileAccessTab, setMobileAccessTab] = useState<"alert" | "access">(
    accessView === "true" ? "access" : "alert",
  );

  const alertsOrDrafts = currentItem?.is_draft === 1 ? "drafts" : "alerts";

  if (isSmall && searchParams.get("bulletinId") != null && !isAlertSingleView) {
    navigate(
      `/rubialert/${alertsOrDrafts}/${searchParams.get("bulletinId")}/single`,
    );
  }
  // Back returns to the originating board instead of the generic one, so its
  // filters, search, sorting and view survive the round trip.
  const onExit = () => {
    navigate(returnTo ?? `/rubialert/${alertsOrDrafts}`);
  };

  const getAlertListById = (id: number) => {
    getBulletinById({ path: { bulletin_id: id } })
      .then((res) => {
        setCurrentItem(res.data!);
        setIsLoaded(true);
      })
      .catch((err) => {
        toast.error(err);
      });
  };

  const shareModal = () => {
    if (!isShareModalOpen) return null;
    else
      return (
        <ShareMultipleDialog
          isShareModalOpenArg={isShareModalOpen}
          setIsShareModalOpenArg={setIsShareModalOpen}
          bulletinIDs={[currentItem?.id]}
        />
      );
  };

  const resolveCard = () => {
    if (isResolveOpen) {
      return (
        <ResolveBulletin
          bulletinType={currentItem?.type as any}
          bulletinId={currentItem?.id}
          setIsResolveOpen={setIsResolveOpen}
        />
      );
    }
  };

  const hasNextAlert = () => {
    if (currentItems) {
      let index = currentItems.map((item) => item.id).indexOf(idToUse ?? 0);
      index++;
      // if there's less than 5 alerts, load more page.
      if (index === currentItems.length - 5) {
        queryNextPage();
      }
      return index < currentItems.length;
    }
    return false;
  };

  const hasPreviousAlert = () => {
    if (currentItems) {
      let index = currentItems.map((item) => item.id).indexOf(idToUse ?? 0);
      index--;
      return index >= 0;
    }
    return false;
  };

  const onNextAlert = () => {
    if (currentItems) {
      let index = currentItems.map((item) => item.id).indexOf(idToUse ?? 0);
      index++;
      if (index < currentItems.length) {
        if (slideoutIdOverride) {
          const isShowingSimilar = searchParams.get("similarTo");
          if (isShowingSimilar) {
            setSearchParams((searchParams) => {
              searchParams.set("bulletinId", currentItems[index].id.toString());
              return searchParams;
            });
          } else {
            navigate(`/rubialert/alerts?bulletinId=${currentItems[index].id}`);
          }
        } else {
          navigate(
            `/rubialert/${alertsOrDrafts}/${currentItems[index].id}/single`,
            { state: returnTo ? { from: returnTo } : undefined },
          );
        }
      }
    }
    // if this is the last alert, use queryNextPage
    if (!hasNextAlert()) {
      queryNextPage();
    }
  };

  const onPreviousAlert = () => {
    if (currentItems) {
      let index = currentItems.map((item) => item.id).indexOf(idToUse ?? 0);
      index--;
      if (index >= 0) {
        if (slideoutIdOverride) {
          const isShowingSimilar = searchParams.get("similarTo");
          if (isShowingSimilar) {
            setSearchParams((searchParams) => {
              searchParams.set("bulletinId", currentItems[index].id.toString());
              return searchParams;
            });
          } else {
            navigate(`/rubialert/alerts?bulletinId=${currentItems[index].id}`);
          }
        } else {
          navigate(
            `/rubialert/${alertsOrDrafts}/${currentItems[index].id}/single`,
            { state: returnTo ? { from: returnTo } : undefined },
          );
        }
      }
    }
  };

  // binding left and right keys to next and previous alerts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isEditableTarget =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable;

      if (isEditableTarget) return;

      if (e.key === "ArrowRight") {
        onNextAlert();
      } else if (e.key === "ArrowLeft") {
        onPreviousAlert();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onNextAlert, onPreviousAlert, queryNextPage]);

  const buttonComponents = DisplayAlertSingleTopButton({
    currentItem: currentItem as any,
    mode,
    searchParams,
    setSearchParams,
    readOnly,
    isCreator,
    isResolveOpen,
    setIsResolveOpen,
    hasUnpublishedResolution,
    slideoutIdOverride,
    isSmall,
    hasPreviousAlert,
    hasNextAlert,
    onPreviousAlert,
    onNextAlert,
    onExit,
  });

  // useEffects
  useEffect(() => {
    const isCreator =
      currentUser?.id?.toString() === alertCreatorId?.toString();
    setIsCreator(isCreator);
  }, [currentUser?.id, alertCreatorId]);

  // on navigation to a new alert
  useEffect(() => {
    setIsResolveOpen(false);
    if (Object.keys(currentItem).length > 0) {
      const alertProperties = updateToContextStructure(currentItem as any);
      setAlertProperties(alertProperties);
    }
  }, [currentItem]);

  useEffect(() => {
    if (idToUse) getAlertListById(idToUse);
  }, [idToUse]);

  // Marks the bulletin to scroll back to on the board, so leaving through the
  // Back control or browser back both land on the bulletin being read.
  useEffect(() => {
    if (idToUse) setScrollToBulletinId(idToUse);
  }, [idToUse]);

  if (!isLoaded)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </div>
    );

  const displayAlert = currentItem && (
    <DisplayAlert
      mode="add"
      issuing_police_service={issuing_police_service?.name ?? ""}
      associatedCrimes={associated_crime}
      occurrenceNumbers={occurrence_number}
      issuedTime={issued_time ?? null}
      description={description}
      pdf={pdf as any}
      evidences={evidence_files as any}
    />
  );

  return (
    <>
      {buttonComponents.topButtons()}
      <div className="alert-container">
        {isResolveOpen && resolveCard()}
        {isSmall ? (
          <div style={{ width: "100%", maxWidth: "100%" }}>
            <Tabs
              value={mobileAccessTab}
              onChange={(_, value) => setMobileAccessTab(value)}
              variant="fullWidth"
            >
              <Tab label="Alert" value="alert" />
              <Tab label="Manage Access" value="access" />
            </Tabs>
            <div
              style={{
                height: "calc(100vh - 190px)",
                width: "100%",
                overflow: "auto",
              }}
            >
              {mobileAccessTab === "alert" ? (
                displayAlert
              ) : (
                <Access bulletinId={idToUse?.toString()} />
              )}
            </div>
          </div>
        ) : accessView === "true" ? (
          <PanelGroup
            orientation="horizontal"
            style={{ height: "calc(100vh - 140px)", width: "100%" }}
          >
            <Panel defaultSize={60} minSize={30} style={{ overflow: "auto" }}>
              {displayAlert}
            </Panel>
            <PanelResizeHandle
              style={{
                width: 8,
                background: colors.divider,
                cursor: "col-resize",
              }}
            />
            <Panel defaultSize={40} minSize={25} style={{ overflow: "auto" }}>
              <Access />
            </Panel>
          </PanelGroup>
        ) : (
          displayAlert
        )}
      </div>
      {isShareModalOpen && shareModal()}
      {isSmall && buttonComponents.mobileBottomButtons()}
      {!isSmall &&
        slideoutIdOverride &&
        buttonComponents.desktopBottomButtons()}
    </>
  );
};
